import { isSimMode } from "./simMode";

/**
 * SIM wallet (Layer 2) — activates the shared E2E wallet harness in sim mode.
 *
 * Shared's `SolomonE2EWalletProvider` (already wrapped around the app in
 * `Layout`) reads `window.__SOLOMON_E2E__`. When `wallet` + `signer` are set it
 * surfaces a connected mock wallet through `useWallet()` and an in-memory
 * "smoke signer" through `useTransactionSigner()` that produces REAL Ed25519
 * `signMessage` signatures — so Step 2 ownership verification (EOA + multisig)
 * works with no browser wallet extension, and the proof is accepted by the
 * Layer-1 mock backend. Step 2, `useWalletVerification`, and shared are all
 * untouched — we only flip the global the harness already reads.
 *
 * Must run BEFORE React renders (the provider reads the global in its first
 * render). The harness itself is gated to non-production hosts, so this is
 * inert in prod even if sim mode were somehow on.
 */

/**
 * Fixed Ed25519 keypair for the sim wallet — public-by-design (it guards
 * nothing real; only the in-memory smoke signer + the mock backend read it).
 * Same deterministic pair as the E2E smoke fixture, so the address is stable.
 * `publicKeyBase58` MUST equal the wallet address or `useWalletVerification`
 * fails its address-mismatch guard.
 */
const SIM_KEYPAIR = {
    publicKeyBase58: "BXq9CTas4DpibqUMCSbMHD5Q3rrNTx3JXekqC4GrKKHb",
    secretKeyBase58: "GAdFhyy8n88RPBg4ogkWqGgUwSz7SxukBbvF4S7aXnQ3Nye2amJJUustT5s5Z8W4u4FfTpb956H6SsLCzXKR5yj"
} as const;

export function initSimWallet(): void {
    if (typeof window === "undefined") return;
    if (!isSimMode()) return;

    const existing = window.__SOLOMON_E2E__ ?? {};
    window.__SOLOMON_E2E__ = {
        ...existing,
        wallet: {
            connected: true,
            isWalletReady: true,
            address: SIM_KEYPAIR.publicKeyBase58,
            walletName: "Sim Wallet"
        },
        signer: {
            publicKeyBase58: SIM_KEYPAIR.publicKeyBase58,
            secretKeyBase58: SIM_KEYPAIR.secretKeyBase58
        }
    };
}
